import { useState, useEffect } from 'react';
import api from '../../../service/apiService';
import './CaseSubmissionModal.css';

// Exported case type constants (used by dashboards when opening the modal)
export const CASE_TYPES = {
  LAW_INQUIRY: 'law_inquiry',
  WILL_REQUEST: 'will_request',
};

const CaseSubmissionModal = ({ isOpen, onClose, caseType, onSubmit, userId }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    urgency: 'medium',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [loadingMethods, setLoadingMethods] = useState(true);

  // ✅ Move useEffect to top level
  useEffect(() => {
    async function fetchMethods() {
      if (!isOpen || !userId) return; // Only fetch when modal opens and we have a user
      setLoadingMethods(true);
      try {
        const res = await api.getPaymentMethods(userId);
        setPaymentMethods(res.paymentMethods || []);
      } catch (err) {
        console.error('Error fetching payment methods:', err);
        setPaymentMethods([]);
      } finally {
        setLoadingMethods(false);
      }
    }

    fetchMethods();
  }, [isOpen, userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileUpload = (event) => {
    const uploadedFiles = Array.from(event.target.files);
    setDocuments((prev) => [...prev, ...uploadedFiles]);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.trim().length < 5) {
      newErrors.title = 'Title must be at least 5 characters';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.trim().length < 20) {
      newErrors.description = 'Description must be at least 20 characters';
    }
    if (!selectedPaymentMethod) {
      newErrors.paymentMethod = 'Please select a payment method';
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title.trim());
      formDataToSend.append('description', formData.description.trim());
      formDataToSend.append('urgency', formData.urgency);
      formDataToSend.append('type', caseType);
      formDataToSend.append('paymentMethodId', selectedPaymentMethod);

      documents.forEach((doc) => {
        formDataToSend.append('documents', doc);
      });

      // Call the onSubmit prop instead of calling API directly
      await onSubmit(formDataToSend);

      // Reset form state
      setFormData({ title: '', description: '', urgency: 'medium' });
      setDocuments([]);
      setErrors({});
      setSelectedPaymentMethod('');
    } catch (error) {
      console.error('Submission error:', error);
      setErrors({ general: error.message || 'Failed to submit case. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({ title: '', description: '', urgency: 'medium' });
      setDocuments([]);
      setErrors({});
      setSelectedPaymentMethod('');
      onClose();
    }
  };

  // Derive display information for the modal based on the provided caseType.
  // If the type isn't recognized, fall back to a generic case submission UI.
  const caseInfo = (() => {
    switch (caseType) {
      case CASE_TYPES.LAW_INQUIRY:
      case 'law_inquiry':
        return {
          title: 'Submit a Law Inquiry',
          subtitle: 'Ask a legal question and our attorneys will review it.',
          placeholder:
            'Please describe your legal question in detail (what happened, dates, parties involved, etc.)',
        };

      case CASE_TYPES.WILL_REQUEST:
      case 'will_request':
        return {
          title: 'Request Will Creation',
          subtitle: 'Provide details for creating your will.',
          placeholder:
            'Please provide information about beneficiaries, assets, and any special requests.',
        };

      default:
        return {
          title: 'Submit a Case',
          subtitle: 'Provide details about your request.',
          placeholder: 'Describe your request in detail so our attorneys can assist you.',
        };
    }
  })();

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{caseInfo.title}</h2>
          <p>{caseInfo.subtitle}</p>
          <button className="modal-close" onClick={handleClose} disabled={isSubmitting}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="case-form">
          {errors.general && <div className="error-banner">{errors.general}</div>}

          {/* Case Title */}
          <div className="form-group">
            <label htmlFor="title">Case Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className={errors.title ? 'error' : ''}
              placeholder="Brief title for your case"
              disabled={isSubmitting}
              maxLength={100}
            />
            {errors.title && <span className="error-message">{errors.title}</span>}
          </div>

          {/* Urgency */}
          <div className="form-group">
            <label htmlFor="urgency">Urgency Level</label>
            <select
              id="urgency"
              name="urgency"
              value={formData.urgency}
              onChange={handleChange}
              disabled={isSubmitting}
            >
              <option value="low">Low - No immediate deadline</option>
              <option value="medium">Medium - Within a few weeks</option>
              <option value="high">High - Within a week</option>
              <option value="urgent">Urgent - ASAP</option>
            </select>
          </div>

          {/* Description */}
          <div className="form-group">
            <label htmlFor="description">Description *</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              className={errors.description ? 'error' : ''}
              placeholder={caseInfo.placeholder}
              disabled={isSubmitting}
              rows={6}
              maxLength={2000}
            />
            <div className="character-count">{formData.description.length}/2000 characters</div>
            {errors.description && <span className="error-message">{errors.description}</span>}
          </div>

          {/* Documents */}
          <div className="form-group">
            <label>Documents</label>
            <ul className="document-list">
              {documents.map((doc, index) => (
                <li key={index}>{doc.name}</li>
              ))}
            </ul>
            <input
              type="file"
              id="file-upload"
              multiple
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
            <button
              type="button"
              className="btn-secondary add-document-btn"
              onClick={() => document.getElementById('file-upload').click()}
            >
              + Add Document
            </button>
          </div>

          {/* Payment Method */}
          <div className="form-group">
            <label htmlFor="payment-method-select">Select Payment Method</label>
            {loadingMethods ? (
              <div className="pm-list-loading">Loading payment methods...</div>
            ) : paymentMethods.length === 0 ? (
              <div className="pm-list-empty">No saved cards found.</div>
            ) : (
              <select
                id="payment-method-select"
                name="paymentMethod"
                value={selectedPaymentMethod}
                onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                disabled={isSubmitting}
                className="pm-dropdown"
              >
                <option value="">-- Select a card --</option>
                {paymentMethods.map((pm) => (
                  <option value={pm.id} key={pm.id}>
                    {pm.brand?.toUpperCase() || 'Card'} •••• {pm.last4} (Exp {pm.exp_month}/
                    {pm.exp_year})
                  </option>
                ))}
              </select>
            )}
            {errors.paymentMethod && <span className="error-message">{errors.paymentMethod}</span>}
          </div>

          {/* Actions */}
          <div className="modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit Case'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CaseSubmissionModal;
